import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { baseStyles, panelStyles, badgeStyles } from '../styles/shared.js';
import type { IngestionJournal, JournalStep } from '../api/ingestion-types.js';

// ---------------------------------------------------------------------------
// <sysml-ingestion-journal> — Timeline-based ingestion audit journal viewer
//
// Renders a step-by-step provenance trail for the ingestion pipeline.
// Shows the active (live) journal session and past completed journals.
// Each step displays metadata: timestamps, token usage, model, decisions.
//
// Pattern: follows sysml-diff-history-panel.ts — baseStyles + panelStyles,
// panel-header layout, scrollable list with expandable items.
// ---------------------------------------------------------------------------

@customElement('sysml-ingestion-journal')
export class SysmlIngestionJournal extends LitElement {
  static override styles = [
    baseStyles,
    panelStyles,
    badgeStyles,
    css`
      :host {
        display: flex;
        flex-direction: column;
        height: 100%;
        overflow: hidden;
      }

      /* ── Header ── */
      .journal-header {
        display: flex;
        align-items: center;
        gap: var(--r5-space-2);
        padding: var(--r5-space-2) var(--r5-space-3);
        border-bottom: 1px solid var(--r5-border);
        flex-shrink: 0;
      }
      .journal-title {
        font-size: var(--r5-text-sm);
        font-weight: 600;
        flex: 1;
      }

      /* ── Scrollable body ── */
      .journal-body {
        flex: 1;
        overflow-y: auto;
        padding: var(--r5-space-2) var(--r5-space-3);
      }

      /* ── Empty state ── */
      .empty-journal {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: var(--r5-space-2);
        padding: var(--r5-space-6);
        color: var(--r5-text-muted);
        text-align: center;
        font-size: var(--r5-text-xs);
      }
      .empty-journal-icon {
        width: 32px;
        height: 32px;
        color: var(--r5-text-muted);
        opacity: 0.5;
      }

      /* ── Session section ── */
      .session-section {
        margin-bottom: var(--r5-space-3);
      }
      .session-header {
        display: flex;
        align-items: center;
        gap: var(--r5-space-2);
        padding: var(--r5-space-1) 0;
        cursor: pointer;
        font-size: var(--r5-text-xs);
        font-weight: 600;
        color: var(--r5-text);
        user-select: none;
      }
      .session-header:hover {
        color: var(--r5-primary);
      }
      .session-chevron {
        width: 12px;
        height: 12px;
        transition: transform var(--r5-duration-fast) var(--r5-ease-default);
        flex-shrink: 0;
      }
      .session-chevron.expanded {
        transform: rotate(90deg);
      }
      .session-meta {
        font-weight: 400;
        color: var(--r5-text-muted);
        margin-left: auto;
        font-size: var(--r5-font-size-badge);
      }

      /* ── Timeline ── */
      .timeline {
        position: relative;
        padding-left: 20px;
        margin-left: 6px;
      }
      .timeline::before {
        content: '';
        position: absolute;
        left: 6px;
        top: 0;
        bottom: 0;
        width: 1px;
        background: var(--r5-border);
      }

      /* ── Step ── */
      .step {
        position: relative;
        padding: var(--r5-space-1) 0 var(--r5-space-2) 0;
      }
      .step-dot {
        position: absolute;
        left: -20px;
        top: 6px;
        width: 13px;
        height: 13px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        background: var(--r5-surface);
        border: 2px solid var(--r5-border);
        z-index: 1;
      }
      .step-dot.completed {
        border-color: var(--r5-success-500);
        background: var(--r5-success-50);
      }
      .step-dot.active {
        border-color: var(--r5-primary);
        background: var(--r5-primary);
        animation: pulse-dot 1.5s ease-in-out infinite;
      }
      .step-dot.error {
        border-color: var(--r5-destructive-500);
        background: var(--r5-destructive-50);
      }
      @keyframes pulse-dot {
        0%, 100% { box-shadow: 0 0 0 0 color-mix(in srgb, var(--r5-primary) 40%, transparent); }
        50% { box-shadow: 0 0 0 4px color-mix(in srgb, var(--r5-primary) 0%, transparent); }
      }

      .step-row {
        display: flex;
        align-items: center;
        gap: var(--r5-space-2);
        cursor: pointer;
      }
      .step-row:hover .step-label {
        color: var(--r5-primary);
      }
      .step-icon {
        width: 14px;
        height: 14px;
        flex-shrink: 0;
        color: var(--r5-text-muted);
      }
      .step-label {
        font-size: var(--r5-text-xs);
        font-weight: 500;
        flex: 1;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        transition: color var(--r5-duration-fast) var(--r5-ease-default);
      }
      .step-duration {
        font-size: var(--r5-font-size-badge);
        font-family: var(--r5-font-mono);
        color: var(--r5-text-muted);
        flex-shrink: 0;
      }

      /* ── Step details (expanded) ── */
      .step-details {
        margin-top: var(--r5-space-1);
        padding: var(--r5-space-2);
        background: color-mix(in srgb, var(--r5-text) 3%, transparent);
        border-radius: var(--r5-radius-md);
        font-size: var(--r5-font-size-badge);
        color: var(--r5-text-muted);
        display: flex;
        flex-direction: column;
        gap: var(--r5-space-1);
      }
      .detail-row {
        display: flex;
        align-items: center;
        gap: var(--r5-space-2);
      }
      .detail-label {
        color: var(--r5-text-muted);
        min-width: 60px;
        flex-shrink: 0;
      }
      .detail-value {
        color: var(--r5-text);
        font-family: var(--r5-font-mono);
      }

      /* ── Token badge ── */
      .token-badge {
        display: inline-flex;
        align-items: center;
        gap: 3px;
        padding: 1px 5px;
        border-radius: var(--r5-radius-sm);
        font-family: var(--r5-font-mono);
        font-size: var(--r5-font-size-badge);
        background: color-mix(in srgb, var(--r5-primary) 8%, transparent);
        color: var(--r5-text-muted);
      }

      /* ── Decision list ── */
      .decision-list {
        display: flex;
        flex-direction: column;
        gap: 2px;
        margin-top: var(--r5-space-1);
      }
      .decision-item {
        display: flex;
        align-items: center;
        gap: var(--r5-space-1);
        font-size: var(--r5-font-size-badge);
      }
      .decision-accepted { color: var(--r5-success-700); }
      .decision-rejected { color: var(--r5-text-muted); }
      .decision-edited { color: var(--r5-primary); }
      .decision-removed { color: var(--r5-destructive-700); }

      /* ── Metrics summary ── */
      .metrics-row {
        display: flex;
        gap: var(--r5-space-3);
        padding: var(--r5-space-2) 0;
        border-top: 1px solid var(--r5-border);
        margin-top: var(--r5-space-1);
        flex-wrap: wrap;
      }
      .metric {
        display: flex;
        align-items: center;
        gap: var(--r5-space-1);
        font-size: var(--r5-font-size-badge);
        color: var(--r5-text-muted);
      }
      .metric-value {
        font-weight: 600;
        color: var(--r5-text);
        font-family: var(--r5-font-mono);
      }

      /* ── Outcome badge ── */
      .outcome-committed {
        background: var(--r5-success-50);
        color: var(--r5-success-700);
        border: 1px solid var(--r5-success-200);
      }
      .outcome-cancelled {
        background: var(--r5-warning-50);
        color: var(--r5-warning-700);
        border: 1px solid var(--r5-warning-200);
      }
      .outcome-error {
        background: var(--r5-destructive-50);
        color: var(--r5-destructive-700);
        border: 1px solid var(--r5-destructive-200);
      }

      /* ── Awaiting indicator ── */
      .awaiting {
        display: flex;
        align-items: center;
        gap: var(--r5-space-2);
        padding: var(--r5-space-1) 0;
        font-size: var(--r5-text-xs);
        color: var(--r5-text-muted);
        font-style: italic;
      }
    `,
  ];

  /** Active (live) ingestion journal */
  @property({ attribute: false }) activeJournal: IngestionJournal | null = null;

  /** Past completed journals */
  @property({ attribute: false }) journals: IngestionJournal[] = [];

  /** Primitive generation counter for reactivity */
  @property({ attribute: false, type: Number }) generation = 0;

  @state() private _expandedSessions = new Set<string>();
  @state() private _expandedStepId: string | null = null;

  override connectedCallback(): void {
    super.connectedCallback();
    // Auto-expand active session
    if (this.activeJournal) {
      this._expandedSessions.add('active');
    }
  }

  override render() {
    const hasActive = !!this.activeJournal;
    const hasPast = this.journals.length > 0;

    if (!hasActive && !hasPast) {
      return html`
        ${this._renderJournalHeader()}
        <div class="journal-body">
          <div class="empty-journal">
            <svg class="empty-journal-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
              <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
            </svg>
            <div>No ingestion sessions recorded yet</div>
            <div>Upload a document to start</div>
          </div>
        </div>
      `;
    }

    return html`
      ${this._renderJournalHeader()}
      <div class="journal-body">
        ${hasActive ? this._renderActiveSession() : nothing}
        ${hasPast ? this._renderPastSessions() : nothing}
      </div>
    `;
  }

  // ── Header ──────────────────────────────────────────────────────────────

  private _renderJournalHeader() {
    return html`
      <div class="journal-header">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
        </svg>
        <span class="journal-title">Ingestion Journal</span>
        ${this.journals.length > 0
          ? html`<span style="font-size: var(--r5-font-size-badge); color: var(--r5-text-muted);">${this.journals.length} past</span>`
          : nothing}
        <ark-button size="xs" variant="ghost" @click=${this._onClose} title="Close journal">
          <ark-icon name="x" size="xs"></ark-icon>
        </ark-button>
      </div>
    `;
  }

  // ── Active session ──────────────────────────────────────────────────────

  private _renderActiveSession() {
    const journal = this.activeJournal!;
    const expanded = this._expandedSessions.has('active');

    return html`
      <div class="session-section">
        <div class="session-header" @click=${() => this._toggleSession('active')}>
          <svg class="session-chevron ${expanded ? 'expanded' : ''}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="9 18 15 12 9 6"/>
          </svg>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--r5-primary)" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <polyline points="12 6 12 12 16 14"/>
          </svg>
          Live Session
          <span class="session-meta">${journal.filename}</span>
          <button
            style="display: flex; align-items: center; justify-content: center; width: 20px; height: 20px; border: none; background: transparent; color: var(--r5-text-muted); cursor: pointer; border-radius: var(--r5-radius-sm); padding: 0; flex-shrink: 0;"
            title="View full report"
            @click=${(e: Event) => { e.stopPropagation(); this._openAuditTrail(journal); }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
            </svg>
          </button>
        </div>

        ${expanded ? html`
          <div class="timeline">
            ${journal.steps.map((step) => this._renderStep(step))}
            ${this._renderAwaitingStep(journal)}
          </div>
          ${journal.steps.length > 0 ? this._renderMetricsSummary(journal) : nothing}
        ` : nothing}
      </div>
    `;
  }

  // ── Past sessions ───────────────────────────────────────────────────────

  private _renderPastSessions() {
    return html`
      ${this.journals.map((journal, index) => {
        const expanded = this._expandedSessions.has(journal.id);
        const outcomeClass = journal.outcome ? `outcome-${journal.outcome}` : '';
        const outcomeLabel = journal.outcome === 'committed' ? 'Committed'
          : journal.outcome === 'cancelled' ? 'Cancelled'
          : journal.outcome === 'error' ? 'Error'
          : 'Unknown';

        return html`
          <div class="session-section">
            <div class="session-header" @click=${() => this._toggleSession(journal.id)}>
              <svg class="session-chevron ${expanded ? 'expanded' : ''}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
              Past: ${journal.filename}
              <span class="session-meta">
                (${index + 1}/${this.journals.length})
              </span>
              <button
                style="display: flex; align-items: center; justify-content: center; width: 20px; height: 20px; border: none; background: transparent; color: var(--r5-text-muted); cursor: pointer; border-radius: var(--r5-radius-sm); padding: 0; flex-shrink: 0;"
                title="View full report"
                @click=${(e: Event) => { e.stopPropagation(); this._openAuditTrail(journal); }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="16" y1="13" x2="8" y2="13"/>
                  <line x1="16" y1="17" x2="8" y2="17"/>
                </svg>
              </button>
            </div>
            ${!expanded ? html`
              <div style="padding-left: 20px; font-size: var(--r5-font-size-badge); color: var(--r5-text-muted); display: flex; gap: var(--r5-space-2); align-items: center; flex-wrap: wrap;">
                <span class="badge ${outcomeClass}">${outcomeLabel}</span>
                <span>${journal.metrics.extractedCount} reqs</span>
                <span>${this._formatDuration(journal.metrics.totalDurationMs)}</span>
              </div>
            ` : nothing}

            ${expanded ? html`
              <div class="timeline">
                ${journal.steps.map((step) => this._renderStep(step))}
              </div>
              ${this._renderMetricsSummary(journal)}
            ` : nothing}
          </div>
        `;
      })}
    `;
  }

  // ── Step rendering ──────────────────────────────────────────────────────

  private _renderStep(step: JournalStep) {
    const expanded = this._expandedStepId === step.id;
    const dotClass = step.kind === 'error' ? 'error' : 'completed';

    return html`
      <div class="step">
        <div class="step-dot ${dotClass}"></div>
        <div class="step-row" @click=${() => this._toggleStep(step.id)}>
          ${this._renderStepIcon(step)}
          <span class="step-label">${this._stepLabel(step)}</span>
          <span class="step-duration">${this._formatDuration(step.durationMs)}</span>
        </div>
        ${expanded ? this._renderStepDetails(step) : nothing}
      </div>
    `;
  }

  private _renderAwaitingStep(journal: IngestionJournal) {
    if (journal.completedAt) return nothing;

    const lastStep = journal.steps[journal.steps.length - 1];
    const awaitingLabel = this._getAwaitingLabel(lastStep);

    return html`
      <div class="step">
        <div class="step-dot active"></div>
        <div class="awaiting">
          ${awaitingLabel}
        </div>
      </div>
    `;
  }

  private _renderStepIcon(step: JournalStep) {
    switch (step.kind) {
      case 'document-uploaded':
        return html`<svg class="step-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
        </svg>`;
      case 'text-extracted':
        return html`<svg class="step-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
        </svg>`;
      case 'requirements-extracted':
        return html`<svg class="step-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
        </svg>`;
      case 'user-review':
        return html`<svg class="step-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/>
        </svg>`;
      case 'duplicate-check':
        return html`<svg class="step-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <rect x="8" y="2" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9"/>
        </svg>`;
      case 'code-generated':
        return html`<svg class="step-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>
        </svg>`;
      case 'user-committed':
        return html`<svg class="step-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
        </svg>`;
      case 'error':
        return html`<svg class="step-icon" viewBox="0 0 24 24" fill="none" stroke="var(--r5-destructive-500)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
        </svg>`;
    }
  }

  private _renderStepDetails(step: JournalStep) {
    switch (step.kind) {
      case 'document-uploaded':
        return html`
          <div class="step-details">
            <div class="detail-row">
              <span class="detail-label">File</span>
              <span class="detail-value">${step.filename}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Type</span>
              <span class="detail-value">${step.sourceType} (${step.mimeType})</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Size</span>
              <span class="detail-value">${this._formatBytes(step.sizeBytes)}</span>
            </div>
          </div>
        `;

      case 'text-extracted':
        return html`
          <div class="step-details">
            <div class="detail-row">
              <span class="detail-label">Method</span>
              <span class="detail-value">${step.method === 'vision' ? 'Claude Vision' : 'Passthrough'}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Length</span>
              <span class="detail-value">${step.textLength.toLocaleString()} chars</span>
            </div>
            ${step.apiUsage ? this._renderUsageBadges(step.apiUsage) : nothing}
          </div>
        `;

      case 'requirements-extracted':
        return html`
          <div class="step-details">
            <div class="detail-row">
              <span class="detail-label">Found</span>
              <span class="detail-value">${step.count} requirements</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Confidence</span>
              <span class="detail-value">${Math.round(step.avgConfidence * 100)}%</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Context</span>
              <span class="detail-value">${step.hadModelContext ? 'Model-aware' : 'Standalone'}</span>
            </div>
            ${step.apiUsage ? this._renderUsageBadges(step.apiUsage) : nothing}
            ${step.warnings.length > 0 ? html`
              <div style="color: var(--r5-warning-700); margin-top: var(--r5-space-1);">
                ${step.warnings.map((w) => html`<div>${w}</div>`)}
              </div>
            ` : nothing}
          </div>
        `;

      case 'user-review':
        return html`
          <div class="step-details">
            <div class="detail-row">
              <span class="detail-label">Accepted</span>
              <span class="detail-value">${step.acceptedCount}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Rejected</span>
              <span class="detail-value">${step.rejectedCount}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Edited</span>
              <span class="detail-value">${step.editedCount}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Removed</span>
              <span class="detail-value">${step.removedCount}</span>
            </div>
            ${step.decisions.length > 0 ? html`
              <div class="decision-list">
                ${step.decisions.map((d) => html`
                  <div class="decision-item decision-${d.action}">
                    ${this._decisionIcon(d.action)}
                    ${d.name} — ${d.action}
                  </div>
                `)}
              </div>
            ` : nothing}
          </div>
        `;

      case 'duplicate-check':
        return html`
          <div class="step-details">
            <div class="detail-row">
              <span class="detail-label">Checked</span>
              <span class="detail-value">${step.checkedCount} requirements</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Duplicates</span>
              <span class="detail-value">${step.duplicatesFoundCount} found</span>
            </div>
            ${step.results.filter((r) => r.duplicateCount > 0).map((r) => html`
              <div class="detail-row">
                <span class="detail-label">${r.name}</span>
                <span class="detail-value">${r.duplicateCount} matches (${Math.round(r.topSimilarity * 100)}%)</span>
              </div>
            `)}
          </div>
        `;

      case 'code-generated':
        return html`
          <div class="step-details">
            <div class="detail-row">
              <span class="detail-label">Method</span>
              <span class="detail-value">${step.method === 'local' ? 'Builder Schema' : 'Claude AI'}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Code</span>
              <span class="detail-value">${step.codeLength.toLocaleString()} chars</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Parse</span>
              <span class="detail-value" style="color: ${step.parseSuccess ? 'var(--r5-success-700)' : 'var(--r5-destructive-700)'};">
                ${step.parseSuccess ? 'Valid' : `${step.diagnosticCount} issues`}
              </span>
            </div>
            ${step.apiUsage ? this._renderUsageBadges(step.apiUsage) : nothing}
          </div>
        `;

      case 'user-committed':
        return html`
          <div class="step-details">
            <div class="detail-row">
              <span class="detail-label">Insert at</span>
              <span class="detail-value">${step.insertionPoint}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Reqs</span>
              <span class="detail-value">${step.requirementCount}</span>
            </div>
          </div>
        `;

      case 'error':
        return html`
          <div class="step-details" style="background: var(--r5-destructive-50);">
            <div class="detail-row">
              <span class="detail-label">Phase</span>
              <span class="detail-value">${step.failedPhase}</span>
            </div>
            <div style="color: var(--r5-destructive-700);">${step.message}</div>
          </div>
        `;
    }
  }

  private _renderUsageBadges(usage: { model: string; inputTokens: number; outputTokens: number; durationMs: number }) {
    return html`
      <div class="detail-row" style="flex-wrap: wrap; gap: var(--r5-space-1);">
        <span class="token-badge">${usage.inputTokens.toLocaleString()} in</span>
        <span class="token-badge">${usage.outputTokens.toLocaleString()} out</span>
        <span class="token-badge">${this._formatDuration(usage.durationMs)}</span>
        <span class="token-badge" style="opacity: 0.7;">${this._shortModel(usage.model)}</span>
      </div>
    `;
  }

  private _renderMetricsSummary(journal: IngestionJournal) {
    const m = journal.metrics;
    return html`
      <div class="metrics-row">
        <div class="metric">
          <span class="metric-value">${this._formatDuration(m.totalDurationMs)}</span> total
        </div>
        ${m.apiCallCount > 0 ? html`
          <div class="metric">
            <span class="metric-value">${m.apiCallCount}</span> API calls
          </div>
          <div class="metric">
            <span class="metric-value">${(m.totalInputTokens + m.totalOutputTokens).toLocaleString()}</span> tokens
          </div>
        ` : nothing}
        ${m.extractedCount > 0 ? html`
          <div class="metric">
            <span class="metric-value">${m.extractedCount}</span> reqs
          </div>
        ` : nothing}
        ${m.avgConfidence > 0 ? html`
          <div class="metric">
            <span class="metric-value">${Math.round(m.avgConfidence * 100)}%</span> conf
          </div>
        ` : nothing}
      </div>
    `;
  }

  // ── Helpers ─────────────────────────────────────────────────────────────

  private _stepLabel(step: JournalStep): string {
    switch (step.kind) {
      case 'document-uploaded': return `Document uploaded`;
      case 'text-extracted': return step.method === 'vision' ? 'Text extracted (vision)' : 'Text loaded';
      case 'requirements-extracted': return `${step.count} requirements found`;
      case 'user-review': return `Review: ${step.acceptedCount} accepted`;
      case 'duplicate-check': return `Duplicates: ${step.duplicatesFoundCount} found`;
      case 'code-generated': return `Code generated (${step.method})`;
      case 'user-committed': return `Committed to editor`;
      case 'error': return `Error: ${step.message.slice(0, 60)}`;
    }
  }

  private _getAwaitingLabel(lastStep: JournalStep | undefined): string {
    if (!lastStep) return 'Starting...';
    switch (lastStep.kind) {
      case 'document-uploaded': return 'Extracting text...';
      case 'text-extracted': return 'Analyzing requirements...';
      case 'requirements-extracted': return 'Awaiting review...';
      case 'user-review': return 'Generating SysML...';
      case 'duplicate-check': return 'Awaiting review...';
      case 'code-generated': return 'Awaiting commit...';
      default: return 'Processing...';
    }
  }

  private _decisionIcon(action: string) {
    switch (action) {
      case 'accepted':
        return html`<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"/></svg>`;
      case 'rejected':
        return html`<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>`;
      case 'edited':
        return html`<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/></svg>`;
      case 'removed':
        return html`<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/></svg>`;
      default:
        return nothing;
    }
  }

  private _formatDuration(ms: number): string {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  }

  private _formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  private _shortModel(model: string): string {
    // "claude-sonnet-4-20250514" → "sonnet-4"
    const match = model.match(/claude-(\w+)-(\d+)/);
    return match ? `${match[1]}-${match[2]}` : model;
  }

  // ── Event handlers ──────────────────────────────────────────────────────

  private _toggleSession(id: string): void {
    const expanded = new Set(this._expandedSessions);
    if (expanded.has(id)) {
      expanded.delete(id);
    } else {
      expanded.add(id);
    }
    this._expandedSessions = expanded;
  }

  private _toggleStep(id: string): void {
    this._expandedStepId = this._expandedStepId === id ? null : id;
  }

  private _openAuditTrail(journal: IngestionJournal): void {
    this.dispatchEvent(new CustomEvent('open-audit-trail', {
      bubbles: true,
      composed: true,
      detail: { journal },
    }));
  }

  private _onClose(): void {
    this.dispatchEvent(new CustomEvent('close-journal', { bubbles: true, composed: true }));
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sysml-ingestion-journal': SysmlIngestionJournal;
  }
}
